import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { JournalImageRecord, JournalRecord, normalizeJournalList } from './journalApi';
import { DEFAULT_JOURNAL_STICKER, getJournalSticker, JOURNAL_STICKERS, JournalSticker, normalizeJournalStickerId } from './journalStickers';
import {
  deleteJournalSchedule,
  findJournalSchedule,
  getCurrentJournalDevice,
  getJournalLayoutInfo,
  getJournalLayoutMap,
  JournalDeviceType,
  JournalLayoutInfo,
  syncJournalSchedule,
} from '../widget/scheduleApi';

const API_BASE = process.env.REACT_APP_API_URL || '';
const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token') || '';
  return {
    'Authorization': `Bearer ${token}`,
  };
};

const resolveImageUrl = (url: string) => {
  if (/^(https?:|blob:|data:)/.test(url)) return url;
  return `${API_BASE}${url.startsWith('/') ? '' : '/'}${url}`;
};

const toDateInputValue = (value?: string) => {
  const parsed = value ? new Date(value) : new Date();
  if (Number.isNaN(parsed.getTime())) return '';
  return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`;
};

// Types
export type Journal = JournalRecord;

// -- Styled Components --
const AppContainer = styled(motion.div)`
  width: 100%;
  height: 100%;
  background: #FDFBF7; /* Samsung Notes warm paper style */
  color: #333;
  display: flex;
  flex-direction: column;
  position: absolute;
  top: 0; left: 0;
  z-index: 100;
  font-family: 'Pretendard', 'Noto Sans KR', sans-serif;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  background: rgba(253, 251, 247, 0.9);
  backdrop-filter: blur(10px);
  z-index: 10;
  border-bottom: 1px solid rgba(0,0,0,0.05);
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  color: #2D2A26;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  color: #555;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
  &:hover {
    background: rgba(0,0,0,0.05);
  }
`;

const EditModeButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 13px;
  border: 1px solid #DED6CA;
  border-radius: 12px;
  background: #FFFDF8;
  color: #3E3933;
  font: inherit;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
`;

const ScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 16px;
`;

const JournalCard = styled(motion.div)<{ $selected?: boolean }>`
  background: #FFFFFF;
  border-radius: 16px;
  padding: 16px;
  border: 2px solid ${({ $selected }) => $selected ? '#E07040' : 'transparent'};
  box-sizing: border-box;
  box-shadow: ${({ $selected }) => $selected
    ? '0 7px 18px rgba(224,112,64,0.16)'
    : '0 4px 12px rgba(0,0,0,0.04), 0 1px 3px rgba(0,0,0,0.02)'};
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  transition: box-shadow 0.2s, transform 0.2s;
  height: 160px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(0,0,0,0.06);
  }
`;

const CardSelectionMark = styled.span<{ $selected: boolean }>`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  border: 1.5px solid ${({ $selected }) => $selected ? '#E07040' : '#CFC7BC'};
  background: ${({ $selected }) => $selected ? '#E07040' : '#FFF'};
  color: #FFF;
  font-size: 13px;
  font-weight: 800;
`;

const CardEmoji = styled.div`
  width: 58px;
  height: 58px;
  align-self: center;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DeviceBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #8A7D6D;
  font-size: 10px;
  font-weight: 700;

  svg { color: #E07040; }
`;

const DeviceIcon: React.FC<{ device: JournalDeviceType; size?: number }> = ({ device, size = 18 }) => device === 'pc' ? (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="4" width="18" height="13" rx="2" />
    <path d="M8 21h8M12 17v4" />
  </svg>
) : (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="7" y="2" width="10" height="20" rx="2.5" />
    <path d="M11 18h2" />
  </svg>
);

const DeviceWarningOverlay = styled(motion.div)`
  position: absolute;
  inset: 0;
  z-index: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(45, 42, 38, 0.42);
  backdrop-filter: blur(3px);
`;

const DeviceWarningCard = styled(motion.div)`
  width: min(360px, 100%);
  padding: 24px;
  border-radius: 22px;
  background: #FFFDF8;
  box-shadow: 0 22px 54px rgba(45, 42, 38, 0.24);
  color: #3D3020;

  .icons { display: flex; align-items: center; justify-content: center; gap: 13px; color: #E07040; margin-bottom: 14px; }
  h3 { margin: 0 0 9px; font-size: 18px; text-align: center; }
  p { margin: 0; color: #7A6D5E; font-size: 13px; line-height: 1.6; text-align: center; }
  .actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 21px; }
  button { padding: 9px 13px; border: 1px solid #DED6CA; border-radius: 11px; background: #FFF; color: #514A42; font: inherit; font-size: 13px; font-weight: 700; cursor: pointer; }
  button.primary { border-color: #E07040; background: #E07040; color: #FFF; }
`;

const CardTitle = styled.div`
  font-weight: 600;
  font-size: 15px;
  color: #333;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardDate = styled.div`
  font-size: 12px;
  color: #888;
  margin-top: auto;
`;

const FAB = styled(motion.button)`
  position: absolute;
  bottom: 32px;
  right: 32px;
  width: 56px;
  height: 56px;
  border-radius: 28px;
  background: #2D2A26;
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 16px rgba(0,0,0,0.2);
  cursor: pointer;
  z-index: 20;
`;

// -- Editor Styles --
const EditorWrapper = styled(motion.div)`
  position: absolute;
  top: 0; left: 0;
  width: 100%; height: 100%;
  background: #FDFBF7;
  display: flex;
  flex-direction: column;
  z-index: 100;
`;

const EditorContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 0 24px 24px;
  overflow-y: auto;
`;

const TitleInput = styled.input`
  font-size: 24px;
  font-weight: 700;
  border: none;
  background: transparent;
  outline: none;
  padding: 16px 0;
  color: #2D2A26;
  font-family: inherit;
  &::placeholder {
    color: #CCC;
  }
`;

const BodyTextarea = styled.textarea`
  position: relative;
  z-index: 1;
  width: 100%;
  min-height: 620px;
  box-sizing: border-box;
  font-size: 16px;
  line-height: 32px;
  border: none;
  background: transparent;
  outline: none;
  resize: none;
  overflow: hidden;
  color: #444;
  font-family: inherit;
  padding: 0 4px 24px;
  &::placeholder {
    color: #CCC;
  }
`;

const ViewerTitle = styled.h2`
  margin: 0;
  padding: 16px 0;
  color: #2D2A26;
  font-size: 24px;
  line-height: 1.35;
  font-weight: 700;
`;

const JournalDateRow = styled.div`
  position: relative;
  min-height: 42px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 9px;
  color: #756B5F;
  font-size: 13px;

`;

const DatePickerButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 11px;
  border: 1px solid #DED6CA;
  border-radius: 11px;
  background: #FFFDF8;
  color: #3E3933;
  font: inherit;
  font-weight: 650;
  cursor: pointer;

  svg { color: #D3784C; }
`;

const DatePickerPopover = styled(motion.div)`
  position: absolute;
  z-index: 300;
  top: 46px;
  right: 0;
  width: 276px;
  box-sizing: border-box;
  padding: 15px;
  border: 1px solid #E4DCCD;
  border-radius: 18px;
  background: #FFFDF8;
  box-shadow: 0 16px 38px rgba(61, 48, 32, 0.18);
`;

const DatePickerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  color: #3D3020;
  font-size: 15px;
  font-weight: 750;

  button {
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: #D3784C;
    font-size: 18px;
    cursor: pointer;
  }
  button:hover { background: rgba(211, 120, 76, 0.1); }
`;

const DatePickerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;

  .weekday {
    padding: 3px 0 6px;
    color: #9A8D7D;
    font-size: 11px;
    font-weight: 700;
    text-align: center;
  }
`;

const DateCellButton = styled.button<{ $selected: boolean; $today: boolean; $outside: boolean }>`
  aspect-ratio: 1;
  padding: 0;
  border: none;
  border-radius: 10px;
  background: ${p => p.$selected ? '#D3784C' : 'transparent'};
  color: ${p => p.$selected ? '#FFF' : p.$outside ? '#C9C0B5' : p.$today ? '#D3784C' : '#514A42'};
  font: inherit;
  font-size: 12px;
  font-weight: ${p => p.$selected || p.$today ? 750 : 500};
  cursor: pointer;

  &:hover { background: ${p => p.$selected ? '#D3784C' : 'rgba(211, 120, 76, 0.11)'}; }
`;

const DatePickerFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;

  button {
    border: none;
    background: transparent;
    color: #D3784C;
    font: inherit;
    font-size: 12px;
    font-weight: 750;
    cursor: pointer;
  }
`;

const ViewerContent = styled.div`
  position: relative;
  z-index: 1;
  box-sizing: border-box;
  width: 100%;
  min-height: 620px;
  padding: 0 4px 24px;
  color: #444;
  font-size: 16px;
  line-height: 32px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

const EmojiSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid rgba(0,0,0,0.05);
`;

const EmojiBtn = styled.button`
  background: #FFF;
  border: 1px solid #E0E0E0;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 20px;
  padding: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0,0,0,0.02);
`;

const EmojiPickerGrid = styled(motion.div)`
  position: absolute;
  top: 60px;
  left: 24px;
  background: white;
  border-radius: 12px;
  padding: 12px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  display: grid;
  grid-template-columns: repeat(3, 80px);
  gap: 8px;
  z-index: 200;
`;

const StickerOptionButton = styled.button<{ $selected: boolean }>`
  width: 80px;
  min-height: 92px;
  padding: 5px 4px 7px;
  border: 2px solid ${p => p.$selected ? '#D3784C' : 'transparent'};
  border-radius: 12px;
  background: ${p => p.$selected ? 'rgba(211, 120, 76, 0.1)' : 'transparent'};
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  color: #665B50;
  font: inherit;
  font-size: 11px;
  font-weight: 650;

  &:hover { background: rgba(211, 120, 76, 0.08); }
`;

const PhotoSection = styled.section`
  margin: 8px 0 18px;
`;

const PhotoToolbar = styled.div<{ $visible: boolean }>`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  min-height: 44px;
  gap: 8px;
  margin-bottom: 10px;
  color: #786F65;
  font-size: 12px;
  visibility: ${p => p.$visible ? 'visible' : 'hidden'};
  pointer-events: ${p => p.$visible ? 'auto' : 'none'};

  .count { margin-left: auto; }
`;

const PhotoButton = styled.button`
  border: 1px solid #DED6CA;
  background: #FFFDF8;
  color: #514A42;
  border-radius: 10px;
  padding: 7px 10px;
  font: inherit;
  font-weight: 650;
  cursor: pointer;

  &:disabled { opacity: 0.45; cursor: default; }
`;

const PhotoBoard = styled.div`
  position: absolute;
  z-index: 2;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
`;

const JournalPaper = styled.div`
  position: relative;
  min-height: 620px;
  border-radius: 6px;
  background-color: rgba(255, 254, 250, 0.72);
  background-image: repeating-linear-gradient(
    to bottom,
    transparent 0,
    transparent 27px,
    rgba(117, 145, 180, 0.14) 27px,
    rgba(117, 145, 180, 0.14) 28px,
    transparent 28px,
    transparent 32px
  );
`;

const TapedPhoto = styled.div<{ $selected: boolean; $interactive: boolean }>`
  position: absolute;
  box-sizing: border-box;
  padding: 7px;
  background: #FFF;
  border: 1px solid rgba(88, 72, 50, 0.12);
  box-shadow: ${p => p.$selected
    ? '0 0 0 2px #D88B5B, 0 12px 25px rgba(61, 48, 32, 0.2)'
    : '0 10px 22px rgba(61, 48, 32, 0.17)'};
  cursor: ${p => p.$interactive ? 'grab' : 'zoom-in'};
  touch-action: ${p => p.$interactive ? 'none' : 'manipulation'};
  user-select: none;
  pointer-events: auto;

  &:active { cursor: grabbing; }

  img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
    pointer-events: none;
  }

  .tape {
    position: absolute;
    z-index: 3;
    width: 36px;
    height: 13px;
    border: 1px solid rgba(150, 122, 67, 0.13);
    background: rgba(237, 215, 158, 0.68);
    box-shadow: inset 0 0 7px rgba(255,255,255,0.42);
    pointer-events: none;
  }
  .tl { top: -7px; left: -13px; transform: rotate(-38deg); }
  .tr { top: -7px; right: -13px; transform: rotate(38deg); }
  .bl { bottom: -7px; left: -13px; transform: rotate(38deg); }
  .br { bottom: -7px; right: -13px; transform: rotate(-38deg); }

  .resize {
    position: absolute;
    z-index: 5;
    right: -8px;
    bottom: -8px;
    width: 22px;
    height: 22px;
    border: 2px solid #FFF;
    border-radius: 50%;
    background: #D88B5B;
    box-shadow: 0 2px 7px rgba(0,0,0,0.2);
    cursor: nwse-resize;
    touch-action: none;
  }

  .rotate {
    position: absolute;
    z-index: 6;
    top: -35px;
    right: -11px;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    border: 2px solid #FFF;
    border-radius: 50%;
    background: #5E7188;
    color: #FFF;
    box-shadow: 0 2px 7px rgba(0,0,0,0.2);
    cursor: grab;
    touch-action: none;
    font-size: 14px;
    line-height: 1;
  }

  .rotate::after {
    content: '';
    position: absolute;
    top: 20px;
    left: 9px;
    width: 2px;
    height: 13px;
    background: #5E7188;
  }

  .rotate:active { cursor: grabbing; }
`;

const SelectedPhotoTools = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 7px;
  margin-top: 10px;
  color: #786F65;
  font-size: 12px;
`;

const EditorError = styled.div`
  margin: 0 0 12px;
  padding: 10px 12px;
  color: #B34D4D;
  background: rgba(208, 96, 96, 0.1);
  border-radius: 10px;
  font-size: 13px;
`;

const PhotoViewerOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  z-index: 12000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 28px;
  background: rgba(27, 24, 21, 0.88);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  cursor: zoom-out;
  touch-action: manipulation;
`;

const PhotoViewerImage = styled(motion.img)`
  display: block;
  max-width: min(94vw, 1600px);
  max-height: 88dvh;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 12px;
  background: #FFF;
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.38);
  cursor: default;
  touch-action: pinch-zoom;
`;

const PhotoViewerClose = styled.button`
  position: fixed;
  top: max(18px, env(safe-area-inset-top));
  right: 20px;
  z-index: 1;
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255,255,255,0.3);
  border-radius: 50%;
  background: rgba(255,255,255,0.14);
  color: #FFF;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
`;

// -- Components --
export const JournalApp: React.FC<{ source: string, onClose: () => void, initialJournalId?: string | null }> = ({ source, onClose, initialJournalId }) => {
  const [journals, setJournals] = useState<Journal[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingJournal, setEditingJournal] = useState<Journal | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [deviceWarningJournal, setDeviceWarningJournal] = useState<Journal | null>(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedJournalIds, setSelectedJournalIds] = useState<Set<string>>(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingSelected, setDeletingSelected] = useState(false);
  const [listError, setListError] = useState('');

  const fetchJournals = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/journal`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        const normalized = normalizeJournalList(data);
        const layoutMap = await getJournalLayoutMap(normalized.map(journal => journal.journal_id))
          .catch(() => ({} as Record<string, JournalLayoutInfo>));
        setJournals(normalized.map(journal => ({
          ...journal,
          emoticon: layoutMap[journal.journal_id]?.sticker || journal.emoticon,
          authoredDevice: layoutMap[journal.journal_id]?.device,
          authoredCanvasWidth: layoutMap[journal.journal_id]?.canvasWidth,
        })));
      }
    } catch (e) {
      console.error('Failed to fetch journals', e);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchJournalDetail = useCallback(async (journal: Journal): Promise<Journal> => {
    try {
      const response = await fetch(`${API_BASE}/journal/${journal.journal_id}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) return journal;
      const payload = await response.json();
      const record = payload?.data ?? payload?.journal ?? payload;
      const detail = normalizeJournalList(record ? [record] : [])[0] ?? journal;
      return {
        ...detail,
        emoticon: getJournalSticker(detail.emoticon) ? detail.emoticon : journal.emoticon || detail.emoticon,
        authoredDevice: journal.authoredDevice,
        authoredCanvasWidth: journal.authoredCanvasWidth,
      };
    } catch {
      return journal;
    }
  }, []);

  const openEditor = useCallback(async (journal?: Journal) => {
    if (journal) {
      const detail = await fetchJournalDetail(journal);
      const layout = detail.authoredDevice
        ? { device: detail.authoredDevice, canvasWidth: detail.authoredCanvasWidth || 0 }
        : await getJournalLayoutInfo(detail.journal_id).catch(() => undefined);
      const enriched = {
        ...detail,
        authoredDevice: layout?.device,
        authoredCanvasWidth: layout?.canvasWidth,
      };
      const shouldWarn = layout?.device &&
        layout.device !== getCurrentJournalDevice() &&
        localStorage.getItem('neurit_journal_device_warning_hidden') !== 'true';
      if (shouldWarn) {
        setDeviceWarningJournal(enriched);
      } else {
        setEditingJournal(enriched);
        setIsCreating(false);
      }
    } else {
      setEditingJournal({
        journal_id: '',
        title: '',
        content: '',
        emoticon: DEFAULT_JOURNAL_STICKER,
        created_at: new Date().toISOString(),
        images: [],
      });
      setIsCreating(true);
    }
  }, [fetchJournalDetail]);

  useEffect(() => {
    fetchJournals();
    if (source === 'journal-write') {
      openEditor();
    }
  }, [fetchJournals, openEditor, source]);

  useEffect(() => {
    if (!initialJournalId || journals.length === 0) return;
    const journal = journals.find(item => item.journal_id === initialJournalId);
    if (journal) {
      openEditor(journal);
    }
  }, [initialJournalId, journals, openEditor]);

  const closeEditor = () => {
    setEditingJournal(null);
    setIsCreating(false);
    fetchJournals();
  };

  const continueOpeningJournal = (neverShowAgain: boolean) => {
    if (!deviceWarningJournal) return;
    if (neverShowAgain) localStorage.setItem('neurit_journal_device_warning_hidden', 'true');
    setEditingJournal(deviceWarningJournal);
    setIsCreating(false);
    setDeviceWarningJournal(null);
  };

  const toggleJournalSelection = (journalId: string) => {
    setSelectedJournalIds(current => {
      const next = new Set(current);
      if (next.has(journalId)) next.delete(journalId);
      else next.add(journalId);
      return next;
    });
  };

  const leaveSelectionMode = () => {
    setSelectionMode(false);
    setSelectedJournalIds(new Set());
    setShowDeleteConfirm(false);
    setListError('');
  };

  const deleteSelectedJournals = async () => {
    const ids = Array.from(selectedJournalIds);
    if (ids.length === 0) return;
    setDeletingSelected(true);
    setListError('');
    const failedIds: string[] = [];
    await Promise.all(ids.map(async journalId => {
      try {
        const response = await fetch(`${API_BASE}/journal/${journalId}`, {
          method: 'DELETE',
          headers: getAuthHeaders(),
        });
        if (!response.ok) throw new Error(`삭제 실패 (${response.status})`);
        await deleteJournalSchedule(journalId).catch(() => undefined);
      } catch (error) {
        console.error('Failed to delete journal', journalId, error);
        failedIds.push(journalId);
      }
    }));
    setShowDeleteConfirm(false);
    setDeletingSelected(false);
    if (failedIds.length > 0) {
      setSelectedJournalIds(new Set(failedIds));
      setListError(`${failedIds.length}개의 일기를 삭제하지 못했습니다. 다시 시도해주세요.`);
    } else {
      setSelectionMode(false);
      setSelectedJournalIds(new Set());
    }
    await fetchJournals();
  };

  return (
    <AppContainer
      initial={{ opacity: 0, scale: 0.94, y: 24 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.94, y: 24 }}
      transition={{ type: 'spring', stiffness: 340, damping: 30 }}
    >
      <Header>
        <Title>내 일기장</Title>
        <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
          {selectionMode ? (
            <>
              <EditModeButton type="button" onClick={leaveSelectionMode}>취소</EditModeButton>
              <EditModeButton
                type="button"
                disabled={selectedJournalIds.size === 0}
                onClick={() => setShowDeleteConfirm(true)}
                style={{ color: selectedJournalIds.size > 0 ? '#B34D4D' : '#BDB5AA' }}
              >
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
                {selectedJournalIds.size}
              </EditModeButton>
            </>
          ) : (
            <EditModeButton type="button" onClick={() => setSelectionMode(true)}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m9 11 3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
              </svg>
              선택
            </EditModeButton>
          )}
          <IconButton onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </IconButton>
        </div>
      </Header>

      <ScrollArea>
        {listError && <EditorError>{listError}</EditorError>}
        {loading ? (
          <div style={{ textAlign: 'center', color: '#999', marginTop: '40px' }}>불러오는 중...</div>
        ) : journals.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#999', marginTop: '40px' }}>작성된 일기가 없습니다.<br/>새로운 일기를 적어보세요!</div>
        ) : (
          <Grid>
            {journals.map((j, index) => (
              <JournalCard
                key={`${j.journal_id}-${index}`}
                $selected={selectedJournalIds.has(j.journal_id)}
                onClick={() => selectionMode ? toggleJournalSelection(j.journal_id) : openEditor(j)}
                layoutId={`journal-${j.journal_id}-${index}`}
              >
                {selectionMode && (
                  <CardSelectionMark $selected={selectedJournalIds.has(j.journal_id)}>
                    {selectedJournalIds.has(j.journal_id) ? '✓' : ''}
                  </CardSelectionMark>
                )}
                <CardTitle>{j.title || '제목 없음'}</CardTitle>
                <CardEmoji aria-label="그날의 기분">
                  <JournalSticker value={j.emoticon} size={58} />
                </CardEmoji>
                <CardDate style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                  <span>{new Date(j.created_at).toLocaleDateString('ko-KR')}</span>
                  {j.authoredDevice && (
                    <DeviceBadge><DeviceIcon device={j.authoredDevice} size={14} />{j.authoredDevice === 'pc' ? 'PC' : '모바일'}</DeviceBadge>
                  )}
                </CardDate>
              </JournalCard>
            ))}
          </Grid>
        )}
      </ScrollArea>

      {!selectionMode && (
        <FAB
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => openEditor()}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
        </FAB>
      )}

      <AnimatePresence>
        {editingJournal && (
          <JournalEditor 
            key="editor"
            journal={editingJournal} 
            isNew={isCreating} 
            onClose={closeEditor} 
          />
        )}
        {deviceWarningJournal && (
          <DeviceWarningOverlay initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <DeviceWarningCard initial={{ opacity: 0, scale: 0.94, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.94, y: 10 }}>
              <div className="icons">
                <DeviceIcon device={deviceWarningJournal.authoredDevice!} size={28} />
                <span>→</span>
                <DeviceIcon device={getCurrentJournalDevice()} size={28} />
              </div>
              <h3>다른 화면에서 작성한 일기예요</h3>
              <p>현재 화면 크기에 맞게 사진과 글을 자동으로 안쪽에 배치합니다. 원본 위치 정보는 변경되지 않습니다.</p>
              <div className="actions">
                <button type="button" onClick={() => continueOpeningJournal(true)}>다시 보지 않기</button>
                <button type="button" className="primary" onClick={() => continueOpeningJournal(false)}>계속 열기</button>
              </div>
            </DeviceWarningCard>
          </DeviceWarningOverlay>
        )}
        {showDeleteConfirm && (
          <DeviceWarningOverlay initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <DeviceWarningCard initial={{ opacity: 0, scale: 0.94, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.94, y: 10 }}>
              <div className="icons">
                <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
              </div>
              <h3>선택한 일기를 삭제할까요?</h3>
              <p>{selectedJournalIds.size}개의 일기가 서버에서 삭제되며 복구할 수 없습니다.</p>
              <div className="actions">
                <button type="button" disabled={deletingSelected} onClick={() => setShowDeleteConfirm(false)}>취소</button>
                <button type="button" className="primary" disabled={deletingSelected} onClick={deleteSelectedJournals}>
                  {deletingSelected ? '삭제 중...' : '삭제'}
                </button>
              </div>
            </DeviceWarningCard>
          </DeviceWarningOverlay>
        )}
      </AnimatePresence>
    </AppContainer>
  );
};

// -- Editor Component --
interface EditorPhoto {
  clientId: string;
  serverId?: string;
  src: string;
  file?: File;
  filename?: string;
  x: number;
  y: number;
  rotation: number;
  width: number;
  height: number;
}

type PhotoGesture = {
  type: 'move' | 'resize' | 'rotate';
  id: string;
  pointerX: number;
  pointerY: number;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  centerX?: number;
  centerY?: number;
  startAngle?: number;
};

type TouchPoint = { id: string; x: number; y: number };
type PinchGesture = {
  id: string;
  distance: number;
  angle: number;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
};

const imageRecordToEditorPhoto = (image: JournalImageRecord): EditorPhoto => ({
  clientId: `server-${image.id}`,
  serverId: image.id,
  src: resolveImageUrl(image.image_url),
  filename: image.filename,
  x: image.x_position,
  y: image.y_position,
  rotation: image.z_position,
  width: image.width,
  height: image.height,
});

const JournalEditor: React.FC<{ journal: Journal, isNew: boolean, onClose: () => void }> = ({ journal, isNew, onClose }) => {
  const [journalId, setJournalId] = useState(journal.journal_id);
  const [creating, setCreating] = useState(isNew);
  const [isEditing, setIsEditing] = useState(isNew);
  const [title, setTitle] = useState(journal.title || '');
  const [content, setContent] = useState(journal.content || '');
  const [journalDate, setJournalDate] = useState(() => toDateInputValue(journal.created_at));
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [datePickerMonth, setDatePickerMonth] = useState(() => {
    const initialDate = toDateInputValue(journal.created_at);
    return new Date(`${initialDate}T00:00:00`);
  });
  const [emoticon, setEmoticon] = useState(() => normalizeJournalStickerId(journal.emoticon));
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [photos, setPhotos] = useState<EditorPhoto[]>(() => (journal.images || []).map(imageRecordToEditorPhoto));
  const [previewPhoto, setPreviewPhoto] = useState<EditorPhoto | null>(null);
  const [paperHeight, setPaperHeight] = useState(() => Math.max(
    620,
    ...(journal.images || []).map(image => image.y_position + image.height + 72)
  ));
  const [selectedPhotoId, setSelectedPhotoId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const bodyTextareaRef = useRef<HTMLTextAreaElement>(null);
  const caretPositionRef = useRef(0);
  const journalPaperRef = useRef<HTMLDivElement>(null);
  const photoBoardRef = useRef<HTMLDivElement>(null);
  const gestureRef = useRef<PhotoGesture | null>(null);
  const touchPointsRef = useRef<Map<number, TouchPoint>>(new Map());
  const pinchGestureRef = useRef<PinchGesture | null>(null);
  const photosRef = useRef(photos);
  const [paperWidth, setPaperWidth] = useState(0);

  useEffect(() => {
    photosRef.current = photos;
  }, [photos]);

  useEffect(() => {
    if (!previewPhoto) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setPreviewPhoto(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [previewPhoto]);

  useEffect(() => {
    if (isEditing) setPreviewPhoto(null);
  }, [isEditing]);

  useEffect(() => {
    const paper = journalPaperRef.current;
    if (!paper) return;
    const updateWidth = () => setPaperWidth(paper.clientWidth);
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(paper);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!journalId) return;
    let active = true;
    findJournalSchedule(journalId)
      .then(schedule => {
        if (active && schedule?.date) {
          setJournalDate(schedule.date);
          setDatePickerMonth(new Date(`${schedule.date}T00:00:00`));
        }
      })
      .catch(() => undefined);
    return () => { active = false; };
  }, [journalId]);

  useEffect(() => {
    const textarea = bodyTextareaRef.current;
    if (!textarea || !isEditing) return;
    textarea.style.height = 'auto';
    const contentHeight = Math.max(620, textarea.scrollHeight);
    const photoHeight = photos.reduce((height, photo) => Math.max(height, photo.y + photo.height + 72), 620);
    setPaperHeight(current => Math.max(current, contentHeight, photoHeight));
  }, [content, isEditing, photos]);

  useEffect(() => () => {
    photosRef.current.forEach(photo => {
      if (photo.file && photo.src.startsWith('blob:')) URL.revokeObjectURL(photo.src);
    });
  }, []);

  const updatePhoto = (id: string, updates: Partial<EditorPhoto>) => {
    setPhotos(current => current.map(photo => photo.clientId === id ? { ...photo, ...updates } : photo));
  };

  const handleFilesSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const available = Math.max(0, 3 - photos.length);
    const selectedFiles = Array.from(event.target.files || [])
      .filter(file => file.type.startsWith('image/'))
      .slice(0, available);

    if (selectedFiles.length === 0) {
      event.target.value = '';
      return;
    }

    const baseIndex = photos.length;
    const cursorLine = content.slice(0, caretPositionRef.current).split('\n').length - 1;
    const boardHeight = photoBoardRef.current?.clientHeight ?? 620;
    const insertionY = Math.max(16, Math.min(boardHeight - 140, cursorLine * 32 + 12));
    const added = selectedFiles.map((file, index): EditorPhoto => ({
      clientId: `new-${Date.now()}-${index}`,
      src: URL.createObjectURL(file),
      file,
      filename: file.name,
      x: 24 + ((baseIndex + index) * 34) % 100,
      y: insertionY + index * 18,
      rotation: (baseIndex + index) % 2 === 0 ? -3 : 3,
      width: 168,
      height: 126,
    }));
    setPhotos(current => [...current, ...added]);
    setSelectedPhotoId(added[added.length - 1].clientId);
    setError('');
    event.target.value = '';
  };

  const removeSelectedPhoto = () => {
    if (!selectedPhotoId) return;
    const selected = photos.find(photo => photo.clientId === selectedPhotoId);
    if (selected?.file && selected.src.startsWith('blob:')) URL.revokeObjectURL(selected.src);
    setPhotos(current => current.filter(photo => photo.clientId !== selectedPhotoId));
    setSelectedPhotoId(null);
  };

  const handlePaperTextClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!isEditing) return;
    setSelectedPhotoId(null);

    const textarea = bodyTextareaRef.current;
    if (!textarea) return;
    const paperBounds = event.currentTarget.getBoundingClientRect();
    const targetLine = Math.max(0, Math.floor((event.clientY - paperBounds.top) / 32));
    const lines = content.split('\n');
    const existingLine = lines[targetLine];

    // Text already exists on this row, so preserve the browser's horizontal caret placement.
    if (existingLine) {
      caretPositionRef.current = textarea.selectionStart;
      return;
    }

    let nextContent = content;
    if (targetLine >= lines.length) {
      nextContent += '\n'.repeat(targetLine - lines.length + 1);
      setContent(nextContent);
    }

    const nextLines = nextContent.split('\n');
    const caretPosition = nextLines
      .slice(0, targetLine)
      .reduce((position, line) => position + line.length + 1, 0);
    caretPositionRef.current = caretPosition;
    window.requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(caretPosition, caretPosition);
    });
  };

  const startGesture = (
    event: React.PointerEvent<HTMLElement>,
    photo: EditorPhoto,
    type: PhotoGesture['type']
  ) => {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    setSelectedPhotoId(photo.clientId);

    if (event.pointerType === 'touch' && type === 'move') {
      touchPointsRef.current.set(event.pointerId, {
        id: photo.clientId,
        x: event.clientX,
        y: event.clientY,
      });
      const touches = Array.from(touchPointsRef.current.values()).filter(point => point.id === photo.clientId);
      if (touches.length >= 2) {
        const [first, second] = touches;
        pinchGestureRef.current = {
          id: photo.clientId,
          distance: Math.max(1, Math.hypot(second.x - first.x, second.y - first.y)),
          angle: Math.atan2(second.y - first.y, second.x - first.x),
          x: photo.x,
          y: photo.y,
          width: photo.width,
          height: photo.height,
          rotation: photo.rotation,
        };
        gestureRef.current = null;
        return;
      }
    }

    const gestureElement = type === 'rotate'
      ? event.currentTarget.parentElement ?? event.currentTarget
      : event.currentTarget;
    const bounds = gestureElement.getBoundingClientRect();
    gestureRef.current = {
      type,
      id: photo.clientId,
      pointerX: event.clientX,
      pointerY: event.clientY,
      x: photo.x,
      y: photo.y,
      width: photo.width,
      height: photo.height,
      rotation: photo.rotation,
      ...(type === 'rotate' ? {
        centerX: bounds.left + bounds.width / 2,
        centerY: bounds.top + bounds.height / 2,
        startAngle: Math.atan2(
          event.clientY - (bounds.top + bounds.height / 2),
          event.clientX - (bounds.left + bounds.width / 2)
        ),
      } : {}),
    };
  };

  const moveGesture = (event: React.PointerEvent<HTMLElement>) => {
    const touchPoint = touchPointsRef.current.get(event.pointerId);
    if (event.pointerType === 'touch' && touchPoint) {
      touchPointsRef.current.set(event.pointerId, {
        ...touchPoint,
        x: event.clientX,
        y: event.clientY,
      });
      const touches = Array.from(touchPointsRef.current.values()).filter(point => point.id === touchPoint.id);
      const pinch = pinchGestureRef.current;
      if (touches.length >= 2 && pinch?.id === touchPoint.id) {
        event.preventDefault();
        const [first, second] = touches;
        const distance = Math.max(1, Math.hypot(second.x - first.x, second.y - first.y));
        const angle = Math.atan2(second.y - first.y, second.x - first.x);
        const minimumScale = Math.max(88 / pinch.width, 70 / pinch.height);
        const maximumScale = Math.min(300 / pinch.width, 260 / pinch.height);
        const scale = Math.max(minimumScale, Math.min(maximumScale, distance / pinch.distance));
        const width = Math.round(pinch.width * scale);
        const height = Math.round(pinch.height * scale);
        const boardWidth = photoBoardRef.current?.clientWidth ?? 600;
        const nextY = Math.round(Math.max(0, pinch.y - (height - pinch.height) / 2));
        setPaperHeight(current => Math.max(current, nextY + height + 72));
        updatePhoto(touchPoint.id, {
          x: Math.round(Math.max(0, Math.min(boardWidth - width, pinch.x - (width - pinch.width) / 2))),
          y: nextY,
          width,
          height,
          rotation: Math.round((pinch.rotation + (angle - pinch.angle) * 180 / Math.PI) * 10) / 10,
        });
        return;
      }
    }

    const gesture = gestureRef.current;
    if (!gesture) return;
    event.preventDefault();
    const dx = event.clientX - gesture.pointerX;
    const dy = event.clientY - gesture.pointerY;

    if (gesture.type === 'move') {
      const boardWidth = photoBoardRef.current?.clientWidth ?? 600;
      const nextY = Math.round(Math.max(0, gesture.y + dy));
      setPaperHeight(current => Math.max(current, nextY + gesture.height + 72));
      updatePhoto(gesture.id, {
        x: Math.round(Math.max(0, Math.min(boardWidth - gesture.width, gesture.x + dx))),
        y: nextY,
      });
    } else if (gesture.type === 'resize') {
      const width = Math.round(Math.max(88, Math.min(300, gesture.width + dx)));
      const height = Math.round(Math.max(70, Math.min(260, gesture.height + dy)));
      setPaperHeight(current => Math.max(current, gesture.y + height + 72));
      updatePhoto(gesture.id, { width, height });
    } else if (gesture.centerX != null && gesture.centerY != null && gesture.startAngle != null) {
      const angle = Math.atan2(event.clientY - gesture.centerY, event.clientX - gesture.centerX);
      updatePhoto(gesture.id, {
        rotation: Math.round((gesture.rotation + (angle - gesture.startAngle) * 180 / Math.PI) * 10) / 10,
      });
    }
  };

  const endGesture = (event: React.PointerEvent<HTMLElement>) => {
    if (event.pointerType === 'touch' && touchPointsRef.current.has(event.pointerId)) {
      const endedPoint = touchPointsRef.current.get(event.pointerId)!;
      touchPointsRef.current.delete(event.pointerId);
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      const remaining = Array.from(touchPointsRef.current.entries())
        .filter(([, point]) => point.id === endedPoint.id);
      pinchGestureRef.current = null;
      if (remaining.length === 1) {
        const [, point] = remaining[0];
        const photo = photosRef.current.find(item => item.clientId === point.id);
        if (photo) {
          gestureRef.current = {
            type: 'move',
            id: photo.clientId,
            pointerX: point.x,
            pointerY: point.y,
            x: photo.x,
            y: photo.y,
            width: photo.width,
            height: photo.height,
            rotation: photo.rotation,
          };
        }
      } else {
        gestureRef.current = null;
      }
      return;
    }

    if (gestureRef.current) {
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      gestureRef.current = null;
    }
  };

  const buildJournalFormData = () => {
    const formData = new FormData();
    let nextFileIndex = 0;
    formData.append('title', title.trim());
    formData.append('content', content.trim());
    // Store the stable sticker ID in the existing server emoticon field.
    formData.append('emoticon', emoticon);
    formData.append('images_metadata_json', JSON.stringify(photos.map((photo, index) => {
      const fileIndex = photo.file ? nextFileIndex++ : undefined;
      return {
        ...(photo.serverId ? {
          image_id: Number(photo.serverId),
          id: Number(photo.serverId),
        } : {}),
        ...(photo.filename ? { filename: photo.filename } : {}),
        ...(fileIndex !== undefined ? { file_index: fileIndex } : {}),
        x_position: Math.round(photo.x),
        y_position: Math.round(photo.y),
        z_position: Math.round(photo.rotation * 10) / 10,
        width: Math.round(photo.width),
        height: Math.round(photo.height),
        order: index,
      };
    })));
    photos.forEach(photo => {
      if (photo.file) formData.append('files', photo.file, photo.file.name);
    });
    return formData;
  };

  const handleSave = async () => {
    if (!title.trim() || !content.trim()) {
      setError('제목과 일기 내용을 모두 입력해주세요.');
      return;
    }
    if (!journalDate) {
      setError('일기 날짜를 선택해주세요.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const formData = buildJournalFormData();
      const response = await fetch(`${API_BASE}/journal${creating ? '' : `/${journalId}`}`, {
        method: creating ? 'POST' : 'PUT',
        headers: getAuthHeaders(),
        body: formData,
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        const message = typeof payload?.detail === 'string'
          ? payload.detail
          : `일기 저장에 실패했습니다. (${response.status})`;
        throw new Error(message);
      }
      const savedPayload = await response.json().catch(() => null);
      const savedRecord = savedPayload?.data ?? savedPayload?.journal ?? savedPayload;
      const savedJournal = normalizeJournalList(savedRecord ? [savedRecord] : [])[0];
      const savedJournalId = savedJournal?.journal_id || journalId;
      if (!savedJournalId) throw new Error('저장된 일기 ID를 확인하지 못했습니다.');
      if (savedJournal) {
        setJournalId(savedJournalId);
        setTitle(savedJournal.title);
        setContent(savedJournal.content);
        if (savedJournal.images.length > 0) {
          setPhotos(current => current.map((photo, index) => {
            const savedImage = savedJournal.images[index];
            if (!savedImage) return photo;
            if (photo.file && photo.src.startsWith('blob:')) URL.revokeObjectURL(photo.src);
            return {
              ...photo,
              serverId: savedImage.id,
              src: resolveImageUrl(savedImage.image_url),
              filename: savedImage.filename ?? photo.filename,
              file: undefined,
              // Preserve the exact editor placement. Some API responses return
              // default coordinates immediately after an upload.
              x: photo.x,
              y: photo.y,
              rotation: photo.rotation,
              width: photo.width,
              height: photo.height,
            };
          }));
        }
      }
      setCreating(false);
      await syncJournalSchedule(savedJournalId, journalDate, title.trim(), {
        device: getCurrentJournalDevice(),
        canvasWidth: journal.authoredCanvasWidth || paperWidth || 360,
        sticker: emoticon,
      });
      setSelectedPhotoId(null);
      setShowEmojiPicker(false);
      setShowDatePicker(false);
      setIsEditing(false);
    } catch (e) {
      console.error('Save failed', e);
      setError(e instanceof Error ? e.message : '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (creating) {
      onClose();
      return;
    }
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    
    try {
      const response = await fetch(`${API_BASE}/journal/${journalId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error(`일기 삭제에 실패했습니다. (${response.status})`);
      await deleteJournalSchedule(journalId).catch(() => undefined);
      onClose();
    } catch (e) {
      console.error('Delete failed', e);
      setError(e instanceof Error ? e.message : '삭제에 실패했습니다.');
    }
  };

  const selectedPhoto = photos.find(photo => photo.clientId === selectedPhotoId);
  const shouldReflowPhotos = paperWidth > 0 && (
    (journal.authoredCanvasWidth != null && journal.authoredCanvasWidth > paperWidth + 16)
    || (journal.authoredDevice === 'pc' && getCurrentJournalDevice() === 'mobile')
  );
  const renderedPhotos = new Map<string, { x: number; y: number; width: number; height: number }>();
  const photosByPosition = [...photos].sort((a, b) => a.y - b.y || a.x - b.x);

  photosByPosition.forEach(photo => {
    const maxX = Math.max(8, paperWidth - photo.width - 8);
    const rendered = {
      // 저장 좌표는 건드리지 않고 현재 종이 안쪽의 표시 좌표만 계산한다.
      x: Math.max(8, Math.min(maxX, photo.x)),
      y: Math.max(8, photo.y),
      width: photo.width,
      height: photo.height,
    };

    if (shouldReflowPhotos) {
      const gap = 24;
      const placedPhotos = Array.from(renderedPhotos.values());
      for (let index = 0; index < placedPhotos.length; index += 1) {
        const placed = placedPhotos[index];
        const overlaps = rendered.x < placed.x + placed.width + gap
          && rendered.x + rendered.width + gap > placed.x
          && rendered.y < placed.y + placed.height + gap
          && rendered.y + rendered.height + gap > placed.y;
        if (overlaps) {
          rendered.y = placed.y + placed.height + gap;
        }
      }
    }

    renderedPhotos.set(photo.clientId, rendered);
  });

  const getRenderedPhoto = (photo: EditorPhoto) => {
    return renderedPhotos.get(photo.clientId) || {
      x: photo.x,
      y: photo.y,
      width: photo.width,
      height: photo.height,
    };
  };
  const renderedPaperHeight = Math.max(
    620,
    paperHeight,
    ...photos.map(photo => {
      const rendered = getRenderedPhoto(photo);
      return rendered.y + rendered.height + 72;
    })
  );
  const datePickerYear = datePickerMonth.getFullYear();
  const datePickerMonthIndex = datePickerMonth.getMonth();
  const datePickerFirstDay = new Date(datePickerYear, datePickerMonthIndex, 1).getDay();
  const datePickerStart = new Date(datePickerYear, datePickerMonthIndex, 1 - datePickerFirstDay);
  const datePickerDays = Array.from({ length: 42 }, (_, index) => {
    const date = new Date(datePickerStart);
    date.setDate(datePickerStart.getDate() + index);
    return date;
  });

  const chooseJournalDate = (date: Date) => {
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    setJournalDate(value);
    setDatePickerMonth(new Date(date.getFullYear(), date.getMonth(), 1));
    setShowDatePicker(false);
  };

  return (
    <EditorWrapper
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'spring', damping: 25, stiffness: 200 }}
    >
      <Header style={{ background: 'transparent', borderBottom: 'none' }}>
        <IconButton onClick={onClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </IconButton>
        <div style={{ display: 'flex', gap: '8px' }}>
          {!creating && (
            <IconButton onClick={handleDelete} style={{ color: '#E03030' }} aria-label="일기 삭제">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              </svg>
            </IconButton>
          )}
          {!isEditing ? (
            <EditModeButton onClick={() => setIsEditing(true)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4Z" />
              </svg>
              수정
            </EditModeButton>
          ) : (
            <>
              <IconButton onClick={handleSave} disabled={saving} style={{ color: saving ? '#CCC' : '#2D2A26' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                  <polyline points="17 21 17 13 7 13 7 21"></polyline>
                  <polyline points="7 3 7 8 15 8"></polyline>
                </svg>
              </IconButton>
            </>
          )}
        </div>
      </Header>

      <EditorContent>
        <div style={{ position: 'relative' }}>
          <EmojiSelector>
            <EmojiBtn
              onClick={() => isEditing && setShowEmojiPicker(!showEmojiPicker)}
              disabled={!isEditing}
              style={{ cursor: isEditing ? 'pointer' : 'default' }}
            >
              <JournalSticker value={emoticon} size={36} />
            </EmojiBtn>
            <span style={{ fontSize: '13px', color: '#888' }}>오늘의 감정 스티커</span>
          </EmojiSelector>
          
          <AnimatePresence>
            {showEmojiPicker && (
              <EmojiPickerGrid
                initial={{ opacity: 0, scale: 0.9, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: -10 }}
              >
                {JOURNAL_STICKERS.map(sticker => (
                  <StickerOptionButton
                    key={sticker.id}
                    type="button"
                    $selected={sticker.id === emoticon}
                    aria-label={sticker.label}
                    aria-pressed={sticker.id === emoticon}
                    onClick={() => { setEmoticon(sticker.id); setShowEmojiPicker(false); }}
                  >
                    <JournalSticker value={sticker.id} size={60} />
                    <span>{sticker.label}</span>
                  </StickerOptionButton>
                ))}
              </EmojiPickerGrid>
            )}
          </AnimatePresence>
        </div>

        {isEditing ? (
          <TitleInput
            placeholder="제목"
            value={title}
            maxLength={255}
            onChange={(e) => setTitle(e.target.value)}
          />
        ) : (
          <ViewerTitle>{title}</ViewerTitle>
        )}

        <JournalDateRow>
          <span>일기 날짜</span>
          {isEditing ? (
            <>
              <DatePickerButton
                type="button"
                onClick={() => setShowDatePicker(current => !current)}
                aria-expanded={showDatePicker}
                aria-label="일기 날짜 선택"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="5" width="18" height="16" rx="2" />
                  <path d="M16 3v4M8 3v4M3 11h18" />
                </svg>
                {new Date(`${journalDate}T00:00:00`).toLocaleDateString('ko-KR')}
              </DatePickerButton>

              <AnimatePresence>
                {showDatePicker && (
                  <DatePickerPopover
                    initial={{ opacity: 0, scale: 0.96, y: -6 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.96, y: -6 }}
                    transition={{ duration: 0.16 }}
                    onClick={event => event.stopPropagation()}
                  >
                    <DatePickerHeader>
                      <button
                        type="button"
                        aria-label="이전 달"
                        onClick={() => setDatePickerMonth(new Date(datePickerYear, datePickerMonthIndex - 1, 1))}
                      >‹</button>
                      <span>{datePickerYear}년 {datePickerMonthIndex + 1}월</span>
                      <button
                        type="button"
                        aria-label="다음 달"
                        onClick={() => setDatePickerMonth(new Date(datePickerYear, datePickerMonthIndex + 1, 1))}
                      >›</button>
                    </DatePickerHeader>
                    <DatePickerGrid>
                      {['일', '월', '화', '수', '목', '금', '토'].map(day => (
                        <span key={day} className="weekday">{day}</span>
                      ))}
                      {datePickerDays.map(date => {
                        const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                        const todayValue = toDateInputValue();
                        return (
                          <DateCellButton
                            key={value}
                            type="button"
                            $selected={value === journalDate}
                            $today={value === todayValue}
                            $outside={date.getMonth() !== datePickerMonthIndex}
                            onClick={() => chooseJournalDate(date)}
                          >
                            {date.getDate()}
                          </DateCellButton>
                        );
                      })}
                    </DatePickerGrid>
                    <DatePickerFooter>
                      <button type="button" onClick={() => chooseJournalDate(new Date())}>오늘</button>
                    </DatePickerFooter>
                  </DatePickerPopover>
                )}
              </AnimatePresence>
            </>
          ) : (
            <span>{new Date(`${journalDate}T00:00:00`).toLocaleDateString('ko-KR')}</span>
          )}
        </JournalDateRow>

        {error && <EditorError>{error}</EditorError>}

        <PhotoSection>
          <PhotoToolbar $visible={isEditing} aria-hidden={!isEditing}>
            <PhotoButton
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={!isEditing || photos.length >= 3 || saving}
              tabIndex={isEditing ? 0 : -1}
            >
              + 사진 추가
            </PhotoButton>
            <span className="count">{photos.length}/3</span>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              disabled={!isEditing}
              onChange={handleFilesSelected}
            />
          </PhotoToolbar>

          <JournalPaper
            ref={journalPaperRef}
            style={{ minHeight: renderedPaperHeight }}
            onClick={handlePaperTextClick}
          >
            {photos.length > 0 && (
              <PhotoBoard ref={photoBoardRef}>
                {photos.map((photo, index) => {
                  const rendered = getRenderedPhoto(photo);
                  return (
                    <TapedPhoto
                    key={photo.clientId}
                    $selected={isEditing && photo.clientId === selectedPhotoId}
                    $interactive={isEditing}
                    style={{
                      left: rendered.x,
                      top: rendered.y,
                      width: rendered.width,
                      height: rendered.height,
                      zIndex: photo.clientId === selectedPhotoId ? 20 : index + 1,
                      transform: `rotate(${photo.rotation}deg)`,
                    }}
                    onPointerDown={isEditing ? event => startGesture(event, photo, 'move') : undefined}
                    onPointerMove={isEditing ? moveGesture : undefined}
                    onPointerUp={isEditing ? endGesture : undefined}
                    onPointerCancel={isEditing ? endGesture : undefined}
                    role={!isEditing ? 'button' : undefined}
                    tabIndex={!isEditing ? 0 : undefined}
                    aria-label={!isEditing ? `${photo.filename || `일기 사진 ${index + 1}`} 확대 보기` : undefined}
                    onKeyDown={!isEditing ? event => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setPreviewPhoto(photo);
                      }
                    } : undefined}
                    onClick={event => {
                      event.stopPropagation();
                      if (!isEditing) setPreviewPhoto(photo);
                    }}
                  >
                    <img src={photo.src} alt={photo.filename || `일기 사진 ${index + 1}`} draggable={false} />
                    <span className="tape tl" />
                    <span className="tape tr" />
                    <span className="tape bl" />
                    <span className="tape br" />
                    {isEditing && photo.clientId === selectedPhotoId && (
                      <>
                        <span
                          className="rotate"
                          role="button"
                          aria-label="사진 각도 조절"
                          onPointerDown={event => startGesture(event, photo, 'rotate')}
                          onPointerMove={moveGesture}
                          onPointerUp={endGesture}
                          onPointerCancel={endGesture}
                        >↻</span>
                        <span
                          className="resize"
                          role="button"
                          aria-label="사진 크기 조절"
                          onPointerDown={event => startGesture(event, photo, 'resize')}
                          onPointerMove={moveGesture}
                          onPointerUp={endGesture}
                          onPointerCancel={endGesture}
                        />
                      </>
                    )}
                    </TapedPhoto>
                  );
                })}
              </PhotoBoard>
            )}

            {isEditing ? (
              <BodyTextarea
                ref={bodyTextareaRef}
                placeholder="일기 내용을 작성해주세요..."
                value={content}
                style={{ height: renderedPaperHeight }}
                onChange={(e) => setContent(e.target.value)}
                onSelect={(e) => { caretPositionRef.current = e.currentTarget.selectionStart; }}
              />
            ) : (
              <ViewerContent style={{ minHeight: renderedPaperHeight }}>{content}</ViewerContent>
            )}
          </JournalPaper>

          {isEditing && selectedPhoto && (
            <SelectedPhotoTools>
              <span>모바일에서는 두 손가락으로 확대·회전</span>
              <PhotoButton type="button" onClick={() => updatePhoto(selectedPhoto.clientId, { x: 24, y: 24 })}>위치 초기화</PhotoButton>
              <PhotoButton type="button" onClick={removeSelectedPhoto} style={{ color: '#B34D4D' }}>사진 삭제</PhotoButton>
            </SelectedPhotoTools>
          )}
        </PhotoSection>
      </EditorContent>

      <AnimatePresence>
        {previewPhoto && (
          <PhotoViewerOverlay
            role="dialog"
            aria-modal="true"
            aria-label="일기 사진 원본 보기"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setPreviewPhoto(null)}
          >
            <PhotoViewerClose type="button" aria-label="사진 닫기" onClick={() => setPreviewPhoto(null)}>×</PhotoViewerClose>
            <PhotoViewerImage
              src={previewPhoto.src}
              alt={previewPhoto.filename || '일기 사진 원본'}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.94 }}
              transition={{ type: 'spring', stiffness: 320, damping: 28 }}
              onClick={event => event.stopPropagation()}
            />
          </PhotoViewerOverlay>
        )}
      </AnimatePresence>
    </EditorWrapper>
  );
};

export default JournalApp;
